import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import PaymentDetail from "./PaymentDetail";
import { db } from "@/lib/firebase";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";
import useUserProfile from "@/hooks/useUserProfile";
import useCoopMemberRole from "@/hooks/useCoopMemberRole";
import { ToastContext } from "@/components/ToastProvider";
import { formatDT } from "@/utils/dates";

const STATUSES = ["pending", "verified", "rejected"];

function peso(n) {
  return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Button component for consistent styling
const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  disabled = false, 
  onClick, 
  className = '',
  ...props 
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variants = {
    primary: 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500',
    secondary: 'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500',
    success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    warning: 'bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500',
    outline: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-indigo-500'
  };
  
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };
  
  const classes = `${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`;
  
  return (
    <button className={classes} disabled={disabled} onClick={onClick} {...props}>
      {children}
    </button>
  );
};

// Badge component for consistent status display
const Badge = ({ children, variant = 'default', className = '' }) => {
  const variants = {
    default: 'bg-gray-100 text-gray-800 border-gray-200',
    success: 'bg-green-100 text-green-800 border-green-200',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    danger: 'bg-red-100 text-red-800 border-red-200',
    info: 'bg-blue-100 text-blue-800 border-blue-200'
  };
  
  const classes = `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${variants[variant]} ${className}`;
  
  return <span className={classes}>{children}</span>;
};

export default function MemberPayments() {
  const { profile } = useUserProfile();
  const { isFinancial } = useCoopMemberRole();
  const { notify } = useContext(ToastContext);
  const navigate = useNavigate();
  const isStaff = isFinancial;
  const myUid = profile?.uid || profile?.id || null;

  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [openPaymentId, setOpenPaymentId] = useState("");

  useEffect(() => {
    setLoading(true);
    try {
      // Fetch from both memberPayments and payments collections
      const base1 = collection(db, "memberPayments");
      const base2 = collection(db, "payments");
      
      const filters1 = [];
      const filters2 = [];
      
      if (!isStaff && myUid) {
        filters1.push(where("memberUid", "==", myUid));
        filters2.push(where("uid", "==", myUid)); // payments collection uses 'uid' field
      }
      if (status) {
        // Map 'verified' status to 'paid' for payments collection
        const statusForPayments = status === 'verified' ? 'paid' : status;
        filters1.push(where("status", "==", status));
        filters2.push(where("status", "==", statusForPayments));
      }
      
      const q1 = query(base1, ...filters1, orderBy("createdAt", "desc"));
      const q2 = query(base2, ...filters2, orderBy("createdAt", "desc"));
      
      const unsub1 = onSnapshot(q1, (snap) => {
        const memberPayments = snap.docs.map(d => ({ id: d.id, ...d.data(), source: 'memberPayments' }));
        
        // Get the second collection data
        const unsub2 = onSnapshot(q2, (snap2) => {
          const payments = snap2.docs.map(d => ({ id: d.id, ...d.data(), source: 'payments' }));
          
          // Combine and sort both collections
          const allPayments = [...memberPayments, ...payments];
          allPayments.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
            return dateB - dateA;
          });
          
          setRows(allPayments);
          setLoading(false);
        }, (e) => { 
          // Error fetching payments
          setRows(memberPayments);
          setLoading(false);
        });
        
        return () => unsub2();
      }, (e) => { 
        // Error fetching memberPayments
        setErr(e?.message || String(e)); 
        setLoading(false);
      });
      
      return () => unsub1();
    } catch (e) {
      setErr(String(e));
      setLoading(false);
    }
  }, [status, isStaff, myUid]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="warning">Pending</Badge>;
      case 'verified':
      case 'paid':
        return <Badge variant="success">Verified</Badge>;
      case 'rejected':
        return <Badge variant="danger">Rejected</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const getSourceBadge = (source) => {
    if (source === 'memberPayments') {
      return <Badge variant="info">Member</Badge>;
    } else if (source === 'payments') {
      return <Badge variant="info">Store</Badge>;
    }
    return null;
  };

  if (!isStaff && !myUid) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
        <p className="text-gray-600">You don't have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Status Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <Button
              key={s}
              variant={status === s ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setStatus(s)}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Error Display */}
      {err && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{err}</div>
            </div>
          </div>
        </div>
      )}

      {/* Payments Table */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-flex items-center px-4 py-2 text-sm text-gray-500">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading payments...
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-lg font-medium text-gray-900 mb-2">No payments found</p>
              <p className="text-sm text-gray-600">There are no payments with the selected status.</p>
            </div>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row.createdAt?.toDate ? formatDT(row.createdAt.toDate()) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row.memberName || row.buyerName || row.memberUid || row.uid || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row.paymentType || row.type || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ₱{peso(row.amount || row.grandTotal)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(row.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getSourceBadge(row.source)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setOpenPaymentId(row.id)}
                          disabled={!row.paymentId && !row.id}
                        >
                          Verify
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile List */}
            <div className="sm:hidden p-4 space-y-4">
              {rows.map((row) => (
                <div key={row.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {row.memberName || row.buyerName || row.memberUid || row.uid || '—'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {row.createdAt?.toDate ? formatDT(row.createdAt.toDate()) : '-'}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(row.status)}
                      {getSourceBadge(row.source)}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <p className="text-xs text-gray-500">Type</p>
                      <p className="text-sm font-medium text-gray-900">{row.paymentType || row.type || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Amount</p>
                      <p className="text-sm font-medium text-gray-900">₱{peso(row.amount || row.grandTotal)}</p>
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setOpenPaymentId(row.id)}
                      disabled={!row.paymentId && !row.id}
                    >
                      Verify
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Payment Detail Modal */}
      {openPaymentId && (
        <PaymentDetail
          id={openPaymentId}
          onClose={() => setOpenPaymentId("")}
          adminUid={myUid}
        />
      )}
    </div>
  );
}
